import React from 'react'

import isOffline from '@utilities/offline-mode-util'

import { DataNotFound, ErrorHandler } from './errors'

export interface Payload {
  isLoaded: boolean
  isError: boolean
  status: string
  data: any
  errorStatus?: number
  isFetching: boolean
}

export interface DataHandlerProps<T extends string> {
  children: React.ReactNode
  skeletonFrame?: React.ReactNode
  payloads: { payload: Payload; name: T }[] | Payload
  skip?: { [key in T]?: boolean }
  message?: string
  showDefaultNotFound?: boolean
  className?: string
}

export const UNINITIALIZED_STATUS = 'uninitialized'
export const PENDING_STATUS = 'pending'
export const FULFILLED_STATUS = 'fulfilled'
export const REJECTED_STATUS = 'rejected'

function checkData<T>(data: T) {
  if (!data) {
    return false
  }
  if (Array.isArray(data)) {
    return data.length > 0
  }
  if (typeof data === 'object') {
    return Object.keys(data).length > 0
  }
  return true
}

function useDataHandlerAllComplete<T extends Payload>(payloads: T[]) {
  const activePayload = React.useMemo(() => {
    if (payloads.every((payload) => payload.status === FULFILLED_STATUS)) {
      return payloads[0]
    }

    const anyRejectedPayload = payloads.find(
      (payload) => payload.status === REJECTED_STATUS
    )
    if (anyRejectedPayload) {
      return anyRejectedPayload
    }

    return payloads.find(
      (payload) =>
        payload.status !== FULFILLED_STATUS &&
        payload.status !== REJECTED_STATUS
    ) as T
  }, [payloads])

  return {
    ...activePayload,
    isAllComplete: payloads.every(
      (payload) => payload.status === FULFILLED_STATUS
    ),
    hasError: payloads.some((payload) => payload.status === REJECTED_STATUS),
    hasData: checkData(activePayload?.data),
  }
}

function DataHandler<T extends string>({
  children,
  skeletonFrame,
  payloads,
  skip,
  message = 'This page needs to be setup',
  showDefaultNotFound = true,
  className,
}: DataHandlerProps<T>) {
  const hasMultiplePayloads = Array.isArray(payloads)

  const activePayload = useDataHandlerAllComplete(
    hasMultiplePayloads
      ? payloads
          ?.filter((item) => !skip?.[item.name])
          ?.map((item) => item.payload) || []
      : [payloads]
  )

  if (
    (activePayload.isFetching && !activePayload.hasError) ||
    (!activePayload.hasData &&
      activePayload.status === UNINITIALIZED_STATUS &&
      !isOffline())
  ) {
    if (skeletonFrame) {
      return <div className="h-full w-full">{skeletonFrame}</div>
    }

    return (
      <div className="fixed inset-0 z-10 flex items-center justify-center bg-secondaryColour bg-opacity-50">
        <div className="h-24 w-24 animate-spin rounded-full border-4 border-solid border-neutralColour border-t-transparent text-center"></div>
      </div>
    )
  }

  if (!activePayload.isFetching && !activePayload.hasData) {
    if (activePayload.hasError) {
      if (activePayload.errorStatus === 404) {
        return <DataNotFound message={message} />
      }
      return <ErrorHandler />
    }

    return showDefaultNotFound ? (
      <DataNotFound message={message} />
    ) : (
      <div className={`h-full w-full ${className ?? ''}`}>{children}</div>
    )
  }

  return <div className={`h-full w-full ${className ?? ''}`}>{children}</div>
}

export default DataHandler
